import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, FileText, Clock, CheckCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PatientDashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    submitted: 0,
    under_review: 0,
    opinion_ready: 0,
  });

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const { data } = await axios.get(`${API}/cases`, { withCredentials: true });
      setCases(data.cases || []);
      
      const submitted = data.cases.filter(c => c.status === 'submitted').length;
      const under_review = data.cases.filter(c => c.status === 'under_review').length;
      const opinion_ready = data.cases.filter(c => c.status === 'opinion_ready').length;
      
      setStats({
        total: data.cases.length,
        submitted,
        under_review,
        opinion_ready,
      });
    } catch (error) {
      toast.error('Failed to fetch cases');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      submitted: 'Submitted',
      under_review: 'Under Review',
      opinion_ready: 'Opinion Ready',
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    if (status === 'submitted') return <FileText size={20} weight="duotone" />;
    if (status === 'under_review') return <Clock size={20} weight="duotone" />;
    if (status === 'opinion_ready') return <CheckCircle size={20} weight="duotone" />;
    return null;
  };

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="patient-dashboard">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-manrope text-3xl font-bold text-primary mb-2">Welcome, {user?.name}</h1>
              <p className="text-secondary">Manage your medical consultations</p>
            </div>
            <Link
              to="/submit-case"
              className="btn-primary flex items-center gap-2"
              data-testid="new-case-button"
            >
              <Plus size={20} weight="bold" />
              New Consultation
            </Link>
          </div>

          {/* Video Consultation CTA */}
          <div className="card mb-6" style={{ background: 'linear-gradient(135deg, #4A6750 0%, #38523D 100%)', color: 'white' }} data-testid="video-cta-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-manrope text-lg font-semibold mb-1">Want to talk to a specialist live?</h3>
                <p className="text-sm text-white/80">Book a video consultation with verified Indian specialists.</p>
              </div>
              <Link to="/doctors" className="px-6 py-2 bg-accent text-white rounded-full text-sm font-medium hover:bg-accent/90 whitespace-nowrap" data-testid="patient-browse-doctors">
                Browse Doctors
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="card" data-testid="stat-total-cases">
              <p className="text-sm text-secondary mb-1">Total Cases</p>
              <p className="text-3xl font-bold text-primary">{stats.total}</p>
            </div>
            <div className="card" data-testid="stat-submitted">
              <p className="text-sm text-secondary mb-1">Submitted</p>
              <p className="text-3xl font-bold text-primary">{stats.submitted}</p>
            </div>
            <div className="card" data-testid="stat-under-review">
              <p className="text-sm text-secondary mb-1">Under Review</p>
              <p className="text-3xl font-bold text-accent">{stats.under_review}</p>
            </div>
            <div className="card" data-testid="stat-opinion-ready">
              <p className="text-sm text-secondary mb-1">Opinion Ready</p>
              <p className="text-3xl font-bold" style={{ color: '#3B6953' }}>{stats.opinion_ready}</p>
            </div>
          </div>

          {/* Cases List */}
          <div className="card">
            <h2 className="font-manrope text-xl font-semibold text-primary mb-6">My Consultations</h2>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : cases.length === 0 ? (
              <div className="text-center py-12" data-testid="no-cases-message">
                <FileText size={48} className="text-secondary mx-auto mb-4" weight="duotone" />
                <p className="text-secondary mb-4">No consultations yet</p>
                <Link to="/submit-case" className="btn-primary inline-block">
                  Submit Your First Case
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {cases.map((caseItem, idx) => (
                  <Link
                    key={idx}
                    to={`/case/${caseItem.case_id || idx}`}
                    className="block border border-primary/20 rounded-lg p-6 hover:shadow-md hover:border-primary/40 transition-all"
                    data-testid={`case-item-${idx}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(caseItem.status)}
                        <div>
                          <h3 className="font-manrope font-semibold text-primary">{caseItem.specialty}</h3>
                          <p className="text-sm text-secondary">
                            {new Date(caseItem.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>
                      <span className={`status-badge status-${caseItem.status}`}>
                        {getStatusLabel(caseItem.status)}
                      </span>
                    </div>
                    <p className="text-secondary text-sm line-clamp-2 mb-3">{caseItem.current_diagnosis}</p>
                    {caseItem.status === 'opinion_ready' && (
                      <div className="flex items-center gap-2 text-xs font-medium" style={{ color: '#3B6953' }}>
                        <CheckCircle size={14} weight="fill" />
                        <span>Doctor's opinion ready — tap to read full report</span>
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}